import ms from 'ms'
import { abortableDelay } from './abortable-delay'
import { uploadFile, UploadHttpError, UploadNetworkError } from './upload-file'
import type { UploadFileOptions } from './upload-file'

/** Waits before each retry; the array length is the number of retries. */
const RETRY_DELAYS_MS = [ms('500ms'), ms('1s')]

/** The statuses the AWS SDK and Google Cloud Storage treat as transient; 501 and the other 4xx are final. */
const RETRYABLE_HTTP_STATUSES = new Set([408, 429, 500, 502, 503, 504])

export interface UploadFileWithRetryOptions extends UploadFileOptions {
  /** Called with the number of the attempt about to start, so 2 for the first retry. */
  onRetry?: (attempt: number) => void
}

function isRetryableUploadError(error: unknown): boolean {
  return (
    error instanceof UploadNetworkError ||
    (error instanceof UploadHttpError && RETRYABLE_HTTP_STATUSES.has(error.status))
  )
}

/**
 * `uploadFile` with a bounded retry on a network failure or a transient status from storage. A
 * 4xx is final, and a cancel stops everything, including a wait in progress. Before each retry
 * it reports progress 0, so a progress bar restarts instead of holding at where the last attempt
 * broke off.
 */
export async function uploadFileWithRetry({
  onRetry,
  ...options
}: UploadFileWithRetryOptions): Promise<void> {
  for (let attempt = 1; ; attempt += 1) {
    try {
      await uploadFile(options)
      return
    } catch (error) {
      const retryDelayMs = RETRY_DELAYS_MS[attempt - 1]
      if (retryDelayMs === undefined || !isRetryableUploadError(error)) {
        throw error
      }
      onRetry?.(attempt + 1)
      options.onProgress?.(0, options.file)
      await abortableDelay(retryDelayMs, options.signal)
    }
  }
}
